import React from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { usePlateNumberHistory, useOverlay } from "../../providers";
import { BillPanel, PlateNumberPanel } from "../../components";
import client from "../../client";
import { Bill, BillRaw } from "../../models";
import { useTheme } from "../../hooks/useTheme";
import { NoDataDark, NoDataLight } from "../../assets";
import { FaChevronDown, FaChevronUp, FaMapLocationDot } from "react-icons/fa6";

const BillPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { addPlateNumberToHistory } = usePlateNumberHistory();
  const { plateNumber } = useParams();
  const { setLoading, addToast, clearToast, addDialog } = useOverlay();

  const [canSelectMultiple, setCanSelectMultiple] = React.useState(false);
  const [billList, setBillList] = React.useState<Bill[]>([]);
  const [selectedBills, setSelectedBills] = React.useState<number[]>([]);
  const [inactiveListExpanded, setInactiveListExpanded] = React.useState<boolean>(false);

  const getBillList = React.useCallback(() => {
    if (!plateNumber) {
      navigate(-1);
      return;
    }

    if (billList.length > 0) return;

    setLoading(true);

    addPlateNumberToHistory(plateNumber);

    const body = {
      carStateNum: plateNumber,
    };

    client
      .post("/v1.0/getInvoicesByCarStateNum", body)
      .then((response) => {
        const billListParsed: Bill[] = (response.data.responseData ?? []).map((e: BillRaw) => Bill.fromJson(e));
        setBillList(billListParsed);
        setInactiveListExpanded(billListParsed.every((x) => !x.active));
      })
      .catch((error) => {
        clearToast();
        addToast({ variant: "danger", description: error });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  React.useEffect(() => {
    getBillList();
  }, [getBillList]);

  const activeBillList = billList.filter((x) => x.active);
  const inactiveBillList = billList.filter((x) => !x.active);

  return (
    <div className="md:p-12 md:space-y-12 p-8 space-y-8">
      <div className="flex"><PlateNumberPanel plateNumber={plateNumber} /></div>
      {activeBillList.length > 0 && <div className="text-3xl font-medium flex-start gap-2"><FaMapLocationDot/>{activeBillList[0].parkingName}</div>}
      {billList.length > 0 ? (
        <div className="space-y-4">
          {activeBillList.length > 0 && (
            <>
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold">{t("bill.toPayNow")}</div>
                <div className="flex-1 border-b"></div>
              </div>
              <ul className="space-y-4">
                {activeBillList.map((bill) => {
                  const isSelected = selectedBills.includes(bill.invoiceId);
                  return (
                    <li key={bill.invoiceId}>
                      <BillPanel
                        bill={bill}
                        selectable={canSelectMultiple}
                        selected={isSelected}
                        onClick={() => {
                          if (!canSelectMultiple) return;
                          if (isSelected) {
                            setSelectedBills((prev) => prev.filter((invoiceId) => invoiceId !== bill.invoiceId));
                          } else {
                            setSelectedBills((prev) => [...prev, bill.invoiceId]);
                          }
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {inactiveBillList.length > 0 && (
            <>
              <div
                className="flex items-center gap-4 cursor-pointer"
                onClick={() => setInactiveListExpanded((prev) => !prev)}
              >
                <div className="border bg-destructive/25 text-destructive rounded-lg px-1.5 font-bold">
                  {inactiveBillList.length}
                  {t("common.count")}
                </div>
                <div className="text-2xl font-bold">{t("bill.awaitingPayment")}</div>
                {inactiveListExpanded ? <FaChevronDown /> : <FaChevronUp />}
                <div className="flex-1 border-b"></div>
              </div>
              {inactiveListExpanded && (
                <ul className="space-y-4">
                  {inactiveBillList.map((bill) => {
                    const isSelected = selectedBills.includes(bill.invoiceId);
                    return (
                      <li key={bill.invoiceId}>
                        <BillPanel
                          bill={bill}
                          selectable={canSelectMultiple}
                          selected={isSelected}
                          onClick={() => {
                            if (!canSelectMultiple) return;
                            if (isSelected) {
                              setSelectedBills((prev) => prev.filter((invoiceId) => invoiceId !== bill.invoiceId));
                            } else {
                              setSelectedBills((prev) => [...prev, bill.invoiceId]);
                            }
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex-center p-10 text-primary">
          <img src={theme === "dark" ? NoDataDark : NoDataLight} />
        </div>
      )}
      {selectedBills.length > 1 && (
        <div className="flex-center fixed left-0 bottom-0 w-full p-4 pt-0">
          <button
            type="button"
            className="mt-1 w-full md:w-md text-2xl font-bold bg-primary hover:bg-primary/80 text-primary-foreground py-3 px-6 rounded-xl"
            onClick={() => navigate(`/payment/${selectedBills.join(",")}`)}
          >
            {t("bill.paySelected")} (
            {billList
              .filter((bill) => selectedBills.includes(bill.invoiceId))
              .reduce((sum, bill) => sum + (bill.amt ?? 0), 0)
              .toLocaleString("mn")}
            ₮ )
          </button>
        </div>
      )}
    </div>
  );
};

export default BillPage;

// {
//   selectedBills.length > 0 && (
//     <div className="flex-center">
//       <button
//         type="button"
//         className="mt-1 w-full md:w-md text-2xl font-bold bg-primary hover:bg-primary/80 text-primary-foreground py-3 px-6 rounded-xl"
//       >
//         {t("bill.paySelected")}
//         {/* (
//             {billList
//               .filter((bill) => selectedBills.includes(bill.invoiceId))
//               .reduce((sum, bill) => sum + (bill.amt ?? 0), 0)
//               .toLocaleString("mn")}
//             ₮ ) */}
//       </button>
//     </div>
//   );
// }
